import React, { useEffect, useRef, useState } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { DecodeHintType, BarcodeFormat } from '@zxing/library';
import { klassifiziereScanCodes, normFormat } from '../services/barcodeBatch';
import { barcodesAusFoto } from '../services/barcodeDecode';
import { readBarcodesOnly } from '../services/mlService';
import { useTranslation } from '../hooks/useTranslation';

const SCAN_FORMATS = [
  'code_128', 'itf', 'code_39', 'code_93', 'ean_13', 'ean_8',
  'codabar', 'data_matrix', 'qr_code', 'aztec', 'pdf417',
];

const ZX_MAP = {
  qr_code: BarcodeFormat.QR_CODE,
  code_128: BarcodeFormat.CODE_128,
  code_39: BarcodeFormat.CODE_39,
  code_93: BarcodeFormat.CODE_93,
  itf: BarcodeFormat.ITF,
  ean_13: BarcodeFormat.EAN_13,
  ean_8: BarcodeFormat.EAN_8,
  codabar: BarcodeFormat.CODABAR,
  data_matrix: BarcodeFormat.DATA_MATRIX,
  aztec: BarcodeFormat.AZTEC,
  pdf417: BarcodeFormat.PDF_417,
};

const ZX_REV = {
  0: 'aztec', 1: 'codabar', 2: 'code_39', 3: 'code_93', 4: 'code_128',
  5: 'data_matrix', 6: 'ean_8', 7: 'ean_13', 8: 'itf', 9: 'maxicode',
  10: 'pdf417', 11: 'qr_code',
};

function playTone(kind) {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const now = ctx.currentTime;
    if (kind === 'gruen') {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, now);
      osc.frequency.setValueAtTime(1175, now + 0.09);
      gain.gain.setValueAtTime(0.18, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.24);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.25);
      setTimeout(() => { try { ctx.close(); } catch { /* */ } }, 350);
    } else if (kind === 'rot') {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(210, now);
      osc.frequency.setValueAtTime(175, now + 0.12);
      gain.gain.setValueAtTime(0.15, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.26);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.27);
      setTimeout(() => { try { ctx.close(); } catch { /* */ } }, 350);
    }
  } catch { /* ignore audio errors */ }
}

/**
 * 📷 Live-Barcode-Nachscanner für „Etikett um die Ecke" mit Rot/Grün-Ampel:
 * - 🟥 ROTER BILDSCHIRM bei Leitcode („Leitcode gescannt — weiter zur Sendungsnummer!")
 * - 🟩 GRÜNER BILDSCHIRM bei Sendungsnummer („Sendungsnummer gescannt!" → übernimmt & schließt automatisch)
 */
export default function SideBarcodeScanner({
  active,
  plz = '',
  paketdienst = '',
  recipientName = '',
  lang = 'de',
  onDetected,
  onClose,
}) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const zxingControlsRef = useRef(null);
  const loopRef = useRef(null);
  const serverLoopRef = useRef(null);
  const redResetTimerRef = useRef(null);
  const detectTimerRef = useRef(null);
  const doneRef = useRef(false);
  const lastRedToneAtRef = useRef(0);
  const fileInputRef = useRef(null);
  const { t } = useTranslation();

  // 'suche' | 'leitcode' (ROT) | 'sendung' (GRÜN) | 'fehler'
  const [ampel, setAmpel] = useState('suche');
  const [leitcodeText, setLeitcodeText] = useState('');
  const [sendungText, setSendungText] = useState('');
  const [photoChecking, setPhotoChecking] = useState(false);

  const onDetectedRef = useRef(onDetected);
  onDetectedRef.current = onDetected;
  const plzRef = useRef(plz);
  plzRef.current = plz;
  const dienstRef = useRef(paketdienst);
  dienstRef.current = paketdienst;

  const verarbeiteCodes = (rawList) => {
    if (doneRef.current || !Array.isArray(rawList) || rawList.length === 0) return false;
    const res = klassifiziereScanCodes(rawList, plzRef.current, dienstRef.current);

    if (res.status === 'sendung' && res.tracking) {
      doneRef.current = true;
      if (redResetTimerRef.current) clearTimeout(redResetTimerRef.current);
      setSendungText(res.tracking);
      setAmpel('sendung');
      playTone('gruen');
      try { if (navigator.vibrate) navigator.vibrate(90); } catch { /* */ }
      if (detectTimerRef.current) clearTimeout(detectTimerRef.current);
      detectTimerRef.current = setTimeout(() => {
        detectTimerRef.current = null;
        if (onDetectedRef.current) {
          onDetectedRef.current({
            tracking: res.tracking,
            code: res.code,
            allCodes: rawList,
          });
        }
      }, 650);
      return true;
    }

    if (res.status === 'leitcode') {
      const now = Date.now();
      setLeitcodeText(String(res.raw || '').slice(0, 26));
      setAmpel('leitcode');
      if (now - lastRedToneAtRef.current > 1400) {
        lastRedToneAtRef.current = now;
        playTone('rot');
        try { if (navigator.vibrate) navigator.vibrate([45, 50, 45]); } catch { /* */ }
      }
      if (redResetTimerRef.current) clearTimeout(redResetTimerRef.current);
      redResetTimerRef.current = setTimeout(() => {
        if (!doneRef.current) setAmpel('suche');
      }, 1700);
    }
    return false;
  };

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    doneRef.current = false;
    setAmpel('suche');
    setLeitcodeText('');
    setSendungText('');

    const constraints = {
      audio: false,
      video: {
        facingMode: { ideal: 'environment' },
        width: { ideal: 1920 },
        height: { ideal: 1080 },
      },
    };

    const optimiereFokus = (stream) => {
      try {
        const track = stream?.getVideoTracks?.[0];
        if (!track?.applyConstraints) return;
        const caps = track.getCapabilities ? track.getCapabilities() : {};
        const modi = Array.isArray(caps?.focusMode) ? caps.focusMode : [];
        const wunsch = ['continuous', 'single-shot'].find((m) => modi.includes(m));
        if (wunsch) {
          Promise.resolve(track.applyConstraints({ advanced: [{ focusMode: wunsch }] })).catch(() => {});
        }
      } catch { /* */ }
    };

    // Hintergrund-Unterstützung per Server-ZXing (falls der Barcode sehr fein oder um die Kante gebogen ist)
    const startServerFrameAssist = () => {
      let busy = false;
      const snapAndRead = async () => {
        if (cancelled || doneRef.current) return;
        const v = videoRef.current;
        if (!busy && v && v.videoWidth > 200 && v.videoHeight > 200) {
          busy = true;
          try {
            const maxKante = 1400;
            const scale = Math.min(1, maxKante / Math.max(v.videoWidth, v.videoHeight));
            const c = document.createElement('canvas');
            c.width = Math.round(v.videoWidth * scale);
            c.height = Math.round(v.videoHeight * scale);
            c.getContext('2d').drawImage(v, 0, 0, c.width, c.height);
            const dataUrl = c.toDataURL('image/jpeg', 0.82);
            const bin = atob(dataUrl.split(',')[1]);
            const bytes = new Uint8Array(bin.length);
            for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
            const blob = new Blob([bytes], { type: 'image/jpeg' });
            const serverCodes = await readBarcodesOnly(blob);
            if (!cancelled && !doneRef.current && serverCodes.length > 0) {
              verarbeiteCodes(serverCodes);
            }
          } catch { /* ignore */ }
          busy = false;
        }
        if (!cancelled && !doneRef.current) {
          serverLoopRef.current = setTimeout(snapAndRead, 1100);
        }
      };
      serverLoopRef.current = setTimeout(snapAndRead, 950);
    };

    const startNativ = async () => {
      if (!window.BarcodeDetector) return false;
      try {
        let nutzbar = SCAN_FORMATS;
        if (window.BarcodeDetector.getSupportedFormats) {
          const sup = await window.BarcodeDetector.getSupportedFormats();
          nutzbar = SCAN_FORMATS.filter((f) => (sup || []).includes(f));
          if (!nutzbar.length) return false;
        }
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return true; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          try { await videoRef.current.play(); } catch { /* */ }
        }
        optimiereFokus(stream);
        const det = new window.BarcodeDetector({ formats: nutzbar });
        const tick = async () => {
          if (cancelled || doneRef.current) return;
          try {
            const res = await det.detect(videoRef.current);
            if (res && res.length) {
              const list = res.map((r) => ({ text: r.rawValue, format: normFormat(r.format) }));
              if (verarbeiteCodes(list)) return;
            }
          } catch { /* */ }
          loopRef.current = setTimeout(tick, 90);
        };
        tick();
        startServerFrameAssist();
        return true;
      } catch {
        return false;
      }
    };

    const startZxing = async () => {
      const hints = new Map();
      hints.set(DecodeHintType.TRY_HARDER, true);
      hints.set(DecodeHintType.POSSIBLE_FORMATS, SCAN_FORMATS.map((f) => ZX_MAP[f]).filter((f) => f !== undefined));
      const reader = new BrowserMultiFormatReader(hints, { delayBetweenScanAttempts: 80 });
      const controls = await reader.decodeFromConstraints(constraints, videoRef.current, (result) => {
        if (result && !cancelled && !doneRef.current) {
          const text = result.getText ? result.getText() : result.text;
          const fmtNum = result.getBarcodeFormat ? result.getBarcodeFormat() : result.format;
          const fmt = ZX_REV[fmtNum] || normFormat(String(fmtNum || 'code_128'));
          verarbeiteCodes([{ text, format: fmt }]);
        }
      });
      if (cancelled) {
        try { Promise.resolve(controls.stop()).catch(() => {}); } catch { /* */ }
        return;
      }
      zxingControlsRef.current = controls;
      optimiereFokus(videoRef.current?.srcObject);
      startServerFrameAssist();
    };

    const start = async (versuch = 0) => {
      try {
        const ok = await startNativ();
        if (ok || cancelled) return;
        await startZxing();
      } catch {
        if (cancelled) return;
        if (versuch === 0) {
          setTimeout(() => start(1), 500);
          return;
        }
        setAmpel('fehler');
      }
    };

    start(0);

    return () => {
      cancelled = true;
      if (loopRef.current) clearTimeout(loopRef.current);
      if (serverLoopRef.current) clearTimeout(serverLoopRef.current);
      if (redResetTimerRef.current) clearTimeout(redResetTimerRef.current);
      if (detectTimerRef.current) { clearTimeout(detectTimerRef.current); detectTimerRef.current = null; }
      try {
        if (zxingControlsRef.current) Promise.resolve(zxingControlsRef.current.stop()).catch(() => {});
      } catch { /* */ }
      try {
        if (streamRef.current) streamRef.current.getTracks().forEach((tr) => tr.stop());
      } catch { /* */ }
      if (videoRef.current) {
        try { videoRef.current.srcObject = null; } catch { /* */ }
      }
    };
    // eslint-disable-next-line
  }, [active]);

  const handleFallbackPhoto = async (e) => {
    const file = e?.target?.files?.[0];
    if (e?.target) e.target.value = '';
    if (!file) return;
    setPhotoChecking(true);
    try {
      const [lokal, server] = await Promise.all([
        barcodesAusFoto(file).catch(() => []),
        readBarcodesOnly(file).catch(() => []),
      ]);
      const alle = [...(Array.isArray(lokal) ? lokal : []), ...(Array.isArray(server) ? server : [])];
      if (!verarbeiteCodes(alle)) {
        // Weder Sendungsnummer noch Leitcode im Foto gefunden
        setLeitcodeText('');
        setAmpel('leitcode');
        playTone('rot');
        if (redResetTimerRef.current) clearTimeout(redResetTimerRef.current);
        redResetTimerRef.current = setTimeout(() => {
          if (!doneRef.current) setAmpel('suche');
        }, 2000);
      }
    } finally {
      setPhotoChecking(false);
    }
  };

  if (!active) return null;

  const isRot = ampel === 'leitcode';
  const isGruen = ampel === 'sendung';
  const borderColor = isGruen ? '#16a34a' : isRot ? '#dc2626' : '#7b61ff';

  return (
    <div
      data-testid="side-barcode-scanner"
      style={{
        margin: '8px 0 12px',
        borderRadius: '12px',
        overflow: 'hidden',
        border: `3px solid ${borderColor}`,
        background: '#0f172a',
        boxShadow: isGruen
          ? '0 0 0 4px rgba(22, 163, 74, 0.35)'
          : isRot
          ? '0 0 0 4px rgba(220, 38, 38, 0.4)'
          : '0 6px 20px rgba(0,0,0,0.35)',
        transition: 'border-color 0.15s, box-shadow 0.15s',
      }}
    >
      {/* Kopfzeile */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 12px',
          background: isGruen ? '#15803d' : isRot ? '#b91c1c' : '#1e293b',
          color: '#fff',
          fontSize: '13px',
          fontWeight: 700,
        }}
      >
        <span>
          {isGruen
            ? `🟩 ${t('ui.scanner.trackingScanned')}`
            : isRot
            ? `🟥 ${t('ui.scanner.routingScannedMove')}`
            : `📷 ${t('ui.scanner.scanSideBarcode')}`}
        </span>
        <button
          type="button"
          onClick={onClose}
          style={{
            background: 'rgba(255,255,255,0.18)',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            padding: '3px 9px',
            fontSize: '12px',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          ✕ {t('common.close')}
        </button>
      </div>

      {/* Kamerabild + Rot/Grün-Ampel-Overlay */}
      <div style={{ position: 'relative', width: '100%', height: '220px', background: '#000' }}>
        <video
          ref={videoRef}
          muted
          playsInline
          style={{
            width: '100%',
            height: '220px',
            objectFit: 'cover',
            display: 'block',
          }}
        />

        {/* Zielrahmen in der Mitte im Suchmodus */}
        {ampel === 'suche' && (
          <div
            style={{
              position: 'absolute',
              inset: '24px 18px',
              border: '2px dashed rgba(255,255,255,0.65)',
              borderRadius: '10px',
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
              paddingBottom: '8px',
            }}
          >
            <span
              style={{
                background: 'rgba(15, 23, 42, 0.82)',
                color: '#fff',
                fontSize: '12px',
                fontWeight: 600,
                padding: '4px 10px',
                borderRadius: '6px',
              }}
            >
              {recipientName
                ? `📦 ${t('ui.scanner.aimNamed', { name: recipientName })}`
                : `📦 ${t('ui.scanner.aim')}`}
            </span>
          </div>
        )}

        {/* 🟥 ROTER BILDSCHIRM: Leitcode erkannt → Weiter schwenken! */}
        {isRot && (
          <div
            data-testid="ampel-rot"
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(220, 38, 38, 0.82)',
              color: '#fff',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '16px',
              textAlign: 'center',
              pointerEvents: 'none',
            }}
          >
            <div style={{ fontSize: '38px', lineHeight: 1, marginBottom: '6px' }}>🟥</div>
            <div style={{ fontSize: '18px', fontWeight: 900, letterSpacing: '0.3px' }}>
              {leitcodeText
                ? t('ui.scanner.routingScannedBig')
                : t('ui.scanner.notTrackingBig')}
            </div>
            {leitcodeText && (
              <div
                style={{
                  fontFamily: 'monospace',
                  fontSize: '13px',
                  background: 'rgba(0,0,0,0.35)',
                  padding: '2px 8px',
                  borderRadius: '5px',
                  marginTop: '4px',
                }}
              >
                {leitcodeText} ({t('ui.scanner.routingCode')})
              </div>
            )}
            <div style={{ fontSize: '14px', fontWeight: 700, marginTop: '8px', lineHeight: 1.35 }}>
              ⬇️ {t('ui.scanner.moveCamera')}
            </div>
          </div>
        )}

        {/* 🟩 GRÜNER BILDSCHIRM: Sendungsnummer erkannt! */}
        {isGruen && (
          <div
            data-testid="ampel-gruen"
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(22, 163, 74, 0.92)',
              color: '#fff',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '16px',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: '44px', lineHeight: 1, marginBottom: '6px' }}>✅</div>
            <div style={{ fontSize: '19px', fontWeight: 900 }}>
              {t('ui.scanner.trackingScannedBig')}
            </div>
            <div
              style={{
                fontFamily: 'monospace',
                fontSize: '16px',
                fontWeight: 800,
                background: 'rgba(0,0,0,0.3)',
                padding: '5px 12px',
                borderRadius: '7px',
                marginTop: '8px',
                letterSpacing: '0.5px',
              }}
            >
              {sendungText}
            </div>
          </div>
        )}

        {/* Kamera-Fehler-Hinweis */}
        {ampel === 'fehler' && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(15, 23, 42, 0.92)',
              color: '#fff',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '16px',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '8px' }}>
              ⚠️ {t('ui.scanner.noLiveCamera')}
            </div>
          </div>
        )}
      </div>

      {/* Fußzeile mit Legende (Rot = Leitcode / Grün = Sendungsnummer) + Foto-Fallback */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '8px',
          padding: '8px 12px',
          background: '#1e293b',
          color: '#cbd5e1',
          fontSize: '12px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 600 }}>
          <span>
            🟥 {t('ui.scanner.legendRed')}
          </span>
          <span>
            🟩 {t('ui.scanner.legendGreen')}
          </span>
        </div>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFallbackPhoto}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            disabled={photoChecking}
            onClick={() => fileInputRef.current?.click()}
            style={{
              background: '#334155',
              color: '#fff',
              border: '1px solid #64748b',
              borderRadius: '6px',
              padding: '5px 10px',
              fontSize: '12px',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {photoChecking
              ? `⏳ ${t('ui.scanner.checkingPhoto')}`
              : `📸 ${t('ui.scanner.orPhoto')}`}
          </button>
        </div>
      </div>
    </div>
  );
}

